import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dom, library } from '@fortawesome/fontawesome-svg-core';
import {
  faSquare,
  faBan,
  faExchange,
  faTags,
} from '@fortawesome/pro-solid-svg-icons';
import { arrow, computePosition, flip, shift, offset } from '@floating-ui/dom';

import { svgWithJs, variables } from '../variables';

@customElement('apt-labeled-icon')
export class AptLabeledIcon extends LitElement {
  @property({ type: String }) color = `gray-600`;
  @property({ type: String }) icon = 'fa-ban';
  @property({ type: Boolean }) enableTooltip = false;
  @property({ type: String }) position = 'bottom';

  @state() button: any;
  @state() tooltip: any;
  @state() arrowElement: any;

  static override get styles() {
    return [
      variables,
      svgWithJs,
      css`
        :host {
          display: flex;
          align-items: center;
          --font-color: #979797;
        }
        #tooltip {
          display: none;
          position: absolute;
          top: 0;
          left: 0;
          background: var(--white);
          color: black;
          padding: 0px;
          pointer-events: none;
          z-index: 100;
        }
        #arrow {
          position: absolute;
          background: var(--white);
          width: 8px;
          height: 8px;
          transform: rotate(45deg);
          border: solid var(--font-color) 1px;
        }
        .interior-fill {
          background-color: var(--white);
          z-index: 100;
          padding: 5px;
        }
      `,
    ];
  }

  override render() {
    return html`
      <slot></slot>
      <span
        id="button"
        style="color: var(--${this.color})"
        class="fa-layers fa-xl"
        @mouseenter="${() => this.showTooltip()}"
        @mouseleave="${() => this.hideTooltip()}"
      >
        <i class="fa-solid fa-square"></i>
        <i
          class="fa-solid fa-inverse ${this.icon}"
          data-fa-transform="shrink-8"
        ></i>
      </span>
      <div id="tooltip" role="tooltip">
        <apt-box>
          <div class="interior-fill">
            <slot name="data-summary"></slot>
          </div>
        </apt-box>
        <div id="arrow"></div>
      </div>
    `;
  }

  setUpTooltip() {
    computePosition(this.button, this.tooltip, {
      placement: `${this.position}` as any,
      middleware: [
        offset(6),
        flip(),
        shift({ padding: 5 }),
        arrow({ element: this.arrowElement }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(this.tooltip.style, {
        left: `${x}px`,
        top: `${y}px`,
      });

      const staticSide: any = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }[placement.split('-')[0]];

      Object.assign(this.arrowElement.style, {
        left:
          middlewareData.arrow?.x != null ? `${middlewareData.arrow?.x}px` : '',
        top:
          middlewareData.arrow?.y != null ? `${middlewareData.arrow?.y}px` : '',
        right: '',
        bottom: '',
        [staticSide]: '-4px',
      });
    });
  }

  showTooltip() {
    if (this.enableTooltip) {
      this.tooltip.style.display = 'block';
      this.setUpTooltip();
    }
  }

  hideTooltip() {
    if (this.enableTooltip) {
      this.tooltip.style.display = 'none';
    }
  }

  protected override firstUpdated(): void {
    library.add(faSquare, faBan, faExchange, faTags);
    this.button = this.renderRoot.querySelector('#button');
    this.tooltip = this.renderRoot.querySelector('#tooltip');
    this.arrowElement = this.renderRoot.querySelector('#arrow');

    // @ts-ignore The dom.watch() TS definition indicates no parameters for this. However, when
    // you actually dig into the code, you see that it looks at the arguments after it gets into
    // the code and tries to pull some out if there aren't any (it even supplies an empty object
    // for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created.
    dom.watch({
      autoReplaceSvgRoot: this.shadowRoot,
      observeMutationsRoot: this.shadowRoot,
    });
  }
}
